import React from 'react'

import { Box, useTheme } from '@mui/material'

interface Props {
  children: React.ReactNode
  /**
   * Dimmed tone.
   *
   * The line is what the chip IS on that surface, not which component renders it:
   *
   *   ANNOTATION -> `muted`. The chip labels something else — a nav row, a menu row, a
   *     search field. It is a footnote on that row, and at full contrast it shouted
   *     louder than the label it annotates. Sidebar search + collapse, the Help
   *     popover's rows, the docs search field.
   *   CONTENT -> default. The chip is the subject: the shortcuts dialog and the
   *     Settings -> Controls card exist TO show these keys, so muting them would dim
   *     the point of the surface.
   *
   * This supersedes an earlier note here that put all menus on the default: the Help
   * popover's chips annotate rows, so they belong with the sidebar's, and rendering them
   * brighter than the sidebar's made one shortcut hint look like two different things
   * depending on where you found it.
   */
  muted?: boolean
}

/**
 * A keystroke rendered as a key cap — `⌘K`, `?`, `Esc`.
 *
 * Shared rather than inlined because it has two call sites from day one (the
 * keyboard-shortcuts dialog and the shortcuts card in Settings), and a keystroke
 * chip is exactly the kind of small presentational unit that drifts when copied:
 * one gains a border, the other a different font size, and the two surfaces stop
 * looking like the same system. Extracting at the SECOND hand-roll rather than the
 * fifth is working-conventions §36.
 *
 * Renders `<kbd>` — the element that actually means "keyboard input", so a screen
 * reader and a stylesheet both get the right answer without an ARIA attribute.
 */
/**
 * The chip's rendered box height: py:1 (4+4) + 12px text at line-height 1.4 (16.8)
 * + 1px border top and bottom = 26.8. Exported so anything sitting BESIDE a chip can
 * match its box exactly rather than eyeballing a number that silently drifts the next
 * time the chip's padding changes.
 */
export const KEY_CHIP_HEIGHT = 26.8

export const KeyChip = ({ children, muted = false }: Props) => {
  const theme = useTheme()

  return (
    <Box
      component="kbd"
      sx={{
        flexShrink: 0,
        px: 2,
        py: 1,
        borderRadius: 1,
        // Same border either way — only the fill and the text tone carry the dimming.
        border: `1px solid ${theme.palette.primary[100]}`,
        backgroundColor: muted ? 'transparent' : theme.palette.primary[50],
        color: muted ? theme.palette.primary[400] : theme.palette.text.primary,
        // Lato rather than a monospace stack: the platform ships no mono face, so a
        // `monospace` fallback resolves to whatever the OS picks and looks foreign
        // beside every other label.
        fontFamily: 'Lato',
        fontSize: 12,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Box>
  )
}
